import React, {useEffect, useRef, useState} from 'react';
import Modal from '../../Modal';
import {
    BODY_MAX,
    SUBJECT_MAX,
    countsAgainstCurrentMonth,
    describeRefusal,
    describeSendBlock,
    formatDateTime,
    newIdempotencyKey,
    parseSchedule,
    quotaMonthLabel,
    scheduleBounds,
    sendCapacity,
    timezoneLabel,
} from './newsletterState';

const LOCALE_OPTIONS = [
    {value: '', label: 'Store default'},
    {value: 'en', label: 'English'},
    {value: 'es', label: 'Español'},
    {value: 'fr', label: 'Français'},
];

/**
 * Subject + markdown body + a send-now or schedule choice + a confirm step. The recipient count
 * shown in the confirm step is the count the screen last loaded; the server re-resolves the
 * audience when it sends and its number is what the success state reports. A scheduled send fixes
 * its audience when it is queued, and says so before the seller commits.
 */
export default function NewsletterComposer({api, recipientCount, quota, disabled, onResult}) {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [locale, setLocale] = useState('');
    const [mode, setMode] = useState('now');
    const [scheduleValue, setScheduleValue] = useState('');
    const [confirming, setConfirming] = useState(false);
    // The time the seller reviewed, fixed when they clicked Review so the dialog and the request
    // agree even if the clock moves the live input out of range while the dialog is open.
    const [reviewed, setReviewed] = useState(null);
    const [sending, setSending] = useState(false);
    const [targetAudience, setTargetAudience] = useState(null);
    const [targetError, setTargetError] = useState('');
    const [checking, setChecking] = useState(false);
    const [reviewQuota, setReviewQuota] = useState(null);
    const [reviewCount, setReviewCount] = useState(recipientCount);
    const live = useRef(true);
    useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
    // One key per composed send, reused on retry (replay, not a second send). Editing the draft
    // starts a new send, so the key is dropped on any change.
    const keyRef = useRef(null);

    const edit = setter => event => {
        keyRef.current = null;
        setter(event.target.value);
    };

    const chooseMode = event => {
        keyRef.current = null;
        setMode(event.target.value);
    };

    const trimmedSubject = subject.trim();
    const trimmedBody = body.trim();
    const scheduling = mode === 'schedule';
    // Re-read on every render: the window slides with the clock.
    const schedule = scheduling ? parseSchedule(scheduleValue) : null;
    const targetTime = schedule?.ok ? schedule.scheduledAt : null;
    const creditPath = quota?.totalRemaining !== undefined;
    useEffect(() => {
        let active = true;
        setTargetAudience(null);
        setTargetError('');
        setChecking(Boolean(creditPath && targetTime));
        if (creditPath && targetTime) {
            api.audience(targetTime).then(response => {
                if (active) setTargetAudience({time: targetTime, ...response.data});
            }).catch(() => {
                if (active) setTargetError('Unable to check the delivery month. Please choose the time again.');
            }).finally(() => { if (active) setChecking(false); });
        }
        return () => { active = false; };
    }, [api, creditPath, targetTime, quota]);
    const effectiveQuota = scheduling && creditPath ? targetAudience?.quota : quota;
    const effectiveCount = scheduling && creditPath ? targetAudience?.recipientCount ?? recipientCount : recipientCount;
    const block = creditPath && effectiveQuota
        ? describeSendBlock({recipientCount: effectiveCount, quota: effectiveQuota}) : null;
    const blocked = disabled || Boolean(block) || (creditPath && scheduling
        && (checking || !targetTime || targetAudience?.time !== targetTime || Boolean(targetError)));
    const bounds = scheduleBounds();
    const canReview = Boolean(trimmedSubject) && Boolean(trimmedBody)
        && subject.length <= SUBJECT_MAX && body.length <= BODY_MAX
        && (!scheduling || schedule.ok);

    function review(event) {
        event.preventDefault();
        if (!canReview || blocked) return;
        setReviewed(scheduling ? schedule : null);
        setReviewQuota(effectiveQuota);
        setReviewCount(effectiveCount);
        setConfirming(true);
    }

    async function send() {
        if (sending) return;
        // A scheduled send with no reviewed time must never fall through to an immediate send.
        if (scheduling && !reviewed?.ok) {
            setConfirming(false);
            return;
        }
        keyRef.current = keyRef.current || newIdempotencyKey();
        setSending(true);
        try {
            const response = await api.send(
                {
                    subject: trimmedSubject,
                    body: trimmedBody,
                    locale,
                    scheduledAt: scheduling ? reviewed.scheduledAt : undefined,
                },
                keyRef.current,
            );
            if (!live.current) return;
            setConfirming(false);
            keyRef.current = null;
            setSubject('');
            setBody('');
            setLocale('');
            setMode('now');
            setScheduleValue('');
            onResult({
                type: 'success',
                result: {
                    ...response.data,
                    duplicate: response.meta.outcome === 'duplicate',
                    scheduled: scheduling,
                },
            });
        } catch (err) {
            if (!live.current) return;
            setConfirming(false);
            // A refusal is final for this key; a network failure is not, so keep the key for
            // retry only when the server never gave an answer.
            if (err.status) keyRef.current = null;
            onResult({type: 'error', message: describeRefusal(err)});
        } finally {
            if (live.current) setSending(false);
        }
    }

    const subscribers = `${reviewCount.toLocaleString()} ${
        reviewCount === 1 ? 'subscriber' : 'subscribers'}`;
    // The quota on screen is this month's. A send delivered in a later month counts against that
    // month's allowance, which the server checks, so only a current-month send shows a balance.
    const currentMonth = !reviewed || countsAgainstCurrentMonth(reviewed.scheduledAt, quota);
    const remainingAfter = reviewQuota && (creditPath || currentMonth)
        ? Math.max(sendCapacity(reviewQuota) - reviewCount, 0)
        : null;
    const sendMonth = reviewed && quota ? quotaMonthLabel(reviewed.scheduledAt) : '';

    return (
        <div className="nlm-composer">
            <form onSubmit={review}>
                <div className="group">
                    <label htmlFor="nlm-subject">Subject</label>
                    <input
                        id="nlm-subject"
                        type="text"
                        value={subject}
                        maxLength={SUBJECT_MAX}
                        onChange={edit(setSubject)}
                        required
                    />
                </div>
                <div className="group">
                    <label htmlFor="nlm-body">Message</label>
                    <textarea
                        id="nlm-body"
                        rows={12}
                        value={body}
                        onChange={edit(setBody)}
                        required
                    />
                    <small className="nlm-hint">
                        Markdown is supported. Scripts, styles, and forms are removed. Every
                        email gets a footer with a one-click unsubscribe link.{' '}
                        {body.length.toLocaleString()} / {BODY_MAX.toLocaleString()}
                    </small>
                </div>
                <div className="group">
                    <label htmlFor="nlm-locale">Footer language</label>
                    <select id="nlm-locale" value={locale} onChange={edit(setLocale)}>
                        {LOCALE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                <fieldset className="nlm-schedule">
                    <legend>When to send</legend>
                    <div className="nlm-choices">
                        <label className="checkbox" htmlFor="nlm-mode-now">
                            <input
                                type="radio"
                                id="nlm-mode-now"
                                name="nlm-mode"
                                value="now"
                                checked={!scheduling}
                                onChange={chooseMode}
                            />
                            <span>Send now</span>
                        </label>
                        <label className="checkbox" htmlFor="nlm-mode-schedule">
                            <input
                                type="radio"
                                id="nlm-mode-schedule"
                                name="nlm-mode"
                                value="schedule"
                                checked={scheduling}
                                onChange={chooseMode}
                            />
                            <span>Schedule for later</span>
                        </label>
                    </div>
                    {scheduling && (
                        <div className="group">
                            <label htmlFor="nlm-schedule-at">Send at</label>
                            <input
                                id="nlm-schedule-at"
                                type="datetime-local"
                                value={scheduleValue}
                                min={bounds.minValue}
                                max={bounds.maxValue}
                                onChange={edit(setScheduleValue)}
                                required
                            />
                            <small className="nlm-hint">
                                Times are in your timezone
                                {timezoneLabel() ? ` (${timezoneLabel()})` : ''}. You can schedule
                                up to 30 days ahead.
                            </small>
                            {scheduleValue && !schedule.ok && (
                                <small className="nlm-error" role="alert">{schedule.message}</small>
                            )}
                        </div>
                    )}
                </fieldset>
                <div className="nlm-actions">
                    <button type="submit" className="button" disabled={!canReview || blocked}>
                        {scheduling ? 'Review and schedule' : 'Review and send'}
                    </button>
                </div>
                {creditPath && scheduling && checking && <p className="nlm-hint" role="status">Checking the delivery month…</p>}
                {targetError && <p className="nlm-error" role="alert">{targetError}</p>}
                {creditPath && scheduling && block && <p className="nlm-error" role="alert">{block.message}</p>}
            </form>

            <Modal
                isOpen={confirming}
                onClose={() => !sending && setConfirming(false)}
                title={scheduling ? 'Schedule this newsletter?' : 'Send this newsletter?'}
                size="small"
                footer={(
                    <div className="nlm-actions">
                        <button
                            type="button"
                            className="button secondary"
                            disabled={sending}
                            onClick={() => setConfirming(false)}
                        >
                            Keep editing
                        </button>
                        <button type="button" className="button" disabled={sending} onClick={send}>
                            {sending
                                ? (scheduling ? 'Scheduling…' : 'Sending…')
                                : `${scheduling ? 'Schedule for' : 'Send to'} ${subscribers}`}
                        </button>
                    </div>
                )}
            >
                {creditPath && reviewQuota && <p>
                    This reserves {Math.min(reviewQuota.remaining, reviewCount).toLocaleString()} monthly
                    recipient emails and {Math.max(0, reviewCount - reviewQuota.remaining).toLocaleString()} extra credits now.
                    Extras do not expire at the monthly reset. An eligible cancellation restores the original reservation.
                </p>}
                <p>
                    <strong>{trimmedSubject}</strong>
                </p>
                {scheduling && reviewed?.ok ? (
                    <>
                        <p>
                            This will email {subscribers} on{' '}
                            <strong>{formatDateTime(reviewed.scheduledAt)}</strong>
                            {timezoneLabel() ? ` (${timezoneLabel()})` : ''}.
                            {sendMonth && ` Those emails count against your ${sendMonth} allowance${creditPath ? ' first, then extra credits' : ''}.`}
                            {remainingAfter !== null
                                && (creditPath ? ` You will have ${remainingAfter.toLocaleString()} recipient emails available for ${sendMonth}.`
                                    : ` You will have ${remainingAfter.toLocaleString()} sends left this month.`)}
                        </p>
                        <p>
                            The audience is fixed now. People who confirm their address after you
                            schedule this will not get this issue, and anyone who unsubscribes
                            before it goes out is skipped. You can cancel it until a minute before
                            it starts.
                        </p>
                    </>
                ) : (
                    <p>
                        This will email {subscribers} now and cannot be undone.
                        {remainingAfter !== null
                            && (creditPath ? ` You will have ${remainingAfter.toLocaleString()} recipient emails available in total.`
                                : ` You will have ${remainingAfter.toLocaleString()} sends left this month.`)}
                    </p>
                )}
            </Modal>
        </div>
    );
}
